/**
 * Three-way sync classification for a locally opened file against its linked
 * cloud row. Two hashes alone can say "different" but not WHICH side moved;
 * the link's `lastSyncHash` (what both sides agreed on when the link was
 * created or last reconciled) attributes the change.
 *
 * Pure — unit-tested in __tests__/content/open-sync-state.test.ts.
 */

export type SyncState = 'clean' | 'cloud-newer' | 'local-newer' | 'diverged';

/**
 * @param diskHash     sha256 of the bytes just read from disk (always known).
 * @param cloudHash    sha256 of the linked row's current bytes, or null when
 *                     the row is not content-addressed (legacy path) and its
 *                     bytes are unknown.
 * @param lastSyncHash hash both sides agreed on at the last reconcile, or
 *                     null for a link predating hash tracking.
 */
export function resolveSyncState(
  diskHash: string,
  cloudHash: string | null,
  lastSyncHash: string | null,
): SyncState {
  // Identical bytes are in sync no matter what history says.
  if (cloudHash !== null && diskHash === cloudHash) return 'clean';

  // Unknown cloud bytes or unknown sync point: the sides differ (or might),
  // and the change can't be attributed to one side. Treat as diverged — the
  // conflict dialog offers both directions, so a wrong guess costs a click,
  // never an overwrite.
  if (cloudHash === null || lastSyncHash === null) return 'diverged';

  const diskChanged = diskHash !== lastSyncHash;
  const cloudChanged = cloudHash !== lastSyncHash;
  if (diskChanged && cloudChanged) return 'diverged';
  if (cloudChanged) return 'cloud-newer';
  return 'local-newer';
}
